'use client';

import { useGSAP } from '@gsap/react';
import clsx from 'clsx';
import Image from 'next/image';
import { FC, useRef } from 'react';

import { Typography } from '@/components/shared/Typography';
import VimeoPlayer from '@/components/shared/VimeoPlayer';
import StrapiRichContent from '@/components/StrapiRichContent';
import { gsap } from '@/lib/gsap';

import css from './style.module.scss';
import { HeadingBlockWithTagsVideoDto } from './types';

interface Props {
	data: HeadingBlockWithTagsVideoDto;
	className?: string;
}

const HeadingBlockWithTagsVideo: FC<Props> = ({ data, className }) => {
	const { title, text, tags, image, alt, videoUrl, videoThumbnailUrl } = data;

	const wrapperRef = useRef<HTMLElement>(null);
	const titleRef = useRef<HTMLDivElement>(null);
	const textRef = useRef<HTMLDivElement>(null);
	const tagsRef = useRef<HTMLDivElement>(null);
	const mediaRef = useRef<HTMLDivElement>(null);

	useGSAP(
		() => {
			const section = wrapperRef.current;
			if (!section) return;

			const titleLines = section.querySelectorAll<HTMLElement>(`.${css.title} p`);
			const description = textRef.current;

			if (titleLines?.length)
				gsap.set(titleLines, {
					opacity: 0,
					y: 40,
					filter: 'blur(10px)'
				});

			if (description)
				gsap.set(description, {
					opacity: 0,
					y: 20
				});

			gsap.set(tagsRef.current?.querySelectorAll<HTMLElement>(`.${css.tag}`) ?? [], {
				opacity: 0,
				y: 40,
				filter: 'blur(10px)'
			});

			gsap.set(mediaRef.current, {
				opacity: 0,
				scale: 1.05
			});

			const tlText = gsap.timeline({
				scrollTrigger: {
					trigger: titleLines,
					start: 'top 80%',
					toggleActions: 'play none none reverse'
				}
			});

			if (titleLines?.length)
				tlText.to(titleLines, {
					opacity: 1,
					y: 0,
					filter: 'blur(0px)',
					duration: 1.1,
					ease: 'power3.out',
					stagger: 0.15
				});

			if (description)
				tlText.to(
					description,
					{
						opacity: 1,
						y: 0,
						duration: 1,
						ease: 'power3.out'
					},
					'-=0.8'
				);

			gsap.to(mediaRef.current, {
				opacity: 1,
				scale: 1,
				duration: 1.5,
				ease: 'power2.out',
				scrollTrigger: {
					trigger: mediaRef.current,
					start: 'top 85%',
					toggleActions: 'play none none reverse'
				}
			});

			const tagItems = tagsRef.current
				? Array.from(tagsRef.current.querySelectorAll<HTMLElement>(`.${css.tag}`))
				: [];

			gsap.to(tagItems.length ? tagItems : [tagsRef.current], {
				opacity: 1,
				y: 0,
				filter: 'blur(0px)',
				stagger: 0.1,
				duration: 1,
				ease: 'power3.out',
				scrollTrigger: {
					trigger: tagsRef.current,
					start: 'top 85%',
					toggleActions: 'play none none reverse'
				}
			});
		},
		{ scope: wrapperRef }
	);

	const imageUrl = image?.url;

	return (
		<section ref={wrapperRef} className={clsx(css.section, className)}>
			<div className={clsx(css.container)}>
				{title ? (
					<Typography ref={titleRef} uppercase className={clsx(css.title)} variant="h2" tag="h2">
						<StrapiRichContent content={title} />
					</Typography>
				) : null}

				{tags?.length ? (
					<div ref={tagsRef} className={css.tags}>
						{tags.map((tag, index) => (
							<Typography key={index} variant="button-m" tag="p" className={css.tag}>
								{tag.text}
							</Typography>
						))}
					</div>
				) : null}

				{text ? (
					<Typography ref={textRef} uppercase className={css.text} variant="body-m" tag="div">
						<StrapiRichContent content={text} />
					</Typography>
				) : null}

				{videoUrl || imageUrl ? (
					<div ref={mediaRef} className={css.media}>
						{videoUrl ? (
							<VimeoPlayer
								fitCover
								url={videoUrl}
								thumbnailUrl={videoThumbnailUrl || undefined}
								thumbnailAlt={alt || ''}
								className={clsx(css.video)}
							/>
						) : imageUrl ? (
							<Image
								className={css.image}
								src={imageUrl}
								alt={alt || image?.alternativeText || ''}
								width={1440}
								height={810}
								sizes="(max-width: 991px) 100vw, 70vw"
							/>
						) : null}
					</div>
				) : null}
			</div>
		</section>
	);
};

export default HeadingBlockWithTagsVideo;
